import React, { useRef, useMemo } from 'react';
import type { NumberLineItem } from '../../../../transformers/zod-schemas';
import type { UserAnswerValue } from '../../../types';
import { HighlightBands } from './HighlightBands';
import { CursorBalls, type CursorValidationStatuses } from './CursorBalls';
import { toPercent } from '../../../utils/number-line.utils';

export interface AxisTicksProps {
  min: number;
  max: number;
  majorTicks: number[];
  minorValues: number[];
  showArrowLeft: boolean;
  showArrowRight: boolean;
  highlights: NumberLineItem['highlights'];
  positions: NumberLineItem['positions'];
  cursors?: NumberLineItem['cursors'];
  onChange: (widgetId: string, value: UserAnswerValue) => void;
  values: Record<string, UserAnswerValue>;
  cursorValidationStatuses?: CursorValidationStatuses;
}

/** Static axis row for typed mode: track, arrows, bands, ticks, position dots. */
export const AxisTicks: React.FC<AxisTicksProps> = ({
  min, max,
  majorTicks, minorValues,
  showArrowLeft, showArrowRight,
  highlights, positions, cursors,
  onChange, values, cursorValidationStatuses,
}) => {
  const axisRowRef = useRef<HTMLDivElement>(null);

  // All snap candidates: major ticks + minor tick values
  const snapTicks = useMemo(
    () => [...majorTicks, ...minorValues],
    [majorTicks, minorValues],
  );

  return (
    <div ref={axisRowRef} className="number-line-axis-row" role="presentation">
      <div className="number-line-track" aria-hidden="true" />

      {showArrowLeft  && <div className="number-line-arrow number-line-arrow-left"  aria-hidden="true" />}
      {showArrowRight && <div className="number-line-arrow number-line-arrow-right" aria-hidden="true" />}

      {/* Bands sit below ticks (z-index 1). */}
      <HighlightBands highlights={highlights} min={min} max={max} />

      {/* Cursor fill bands and balls sit above highlights (z-index 2/4). */}
      <CursorBalls
        cursors={cursors}
        min={min}
        max={max}
        snapTicks={snapTicks}
        audibleTicks={majorTicks}
        axisRowRef={axisRowRef}
        onChange={onChange}
        values={values}
        cursorValidationStatuses={cursorValidationStatuses}
      />

      {minorValues.map(v => (
        <div
          key={`minor-${v}`}
          className="number-line-minor-tick"
          aria-hidden="true"
          style={{ left: `${toPercent(v, min, max)}%` }}
        />
      ))}

      {majorTicks.map(tick => (
        <div
          key={`major-${tick}`}
          className="number-line-major-tick"
          aria-hidden="true"
          style={{ left: `${toPercent(tick, min, max)}%` }}
        />
      ))}

      {positions.map(pos => (
        <div
          key={`dot-${pos.id}`}
          className="number-line-position-dot"
          aria-hidden="true"
          style={{ left: `${toPercent(pos.value, min, max)}%` }}
        />
      ))}
    </div>
  );
};
